import { YouTube as YoutubeIcon } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import PlaylistDeleteForeverIcon from '@mui/icons-material/PlaylistRemove';
import { Box, Button, MenuItem } from '@mui/material';
import { apiRequest } from 'api-client';
import apiEndpoints from 'constants/endpoints';
import { KozoomVideo } from 'constants/types';
import moment from 'moment';
import { DataGridListPage } from 'pages/Common';
import { DataGridActionMenu } from 'pages/Common/DataGridActionMenu';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from 'store';
import { deleteDataGridRow, saveRowRankings } from 'store/actions';
import { pageKeys } from 'store/reducers/dataGrid.reducer';
import { getGameName } from 'utils';
import { getEventName } from 'utils/event';
import { formatSeconds } from 'utils/time';
import { getVideoThumbnail } from 'utils/video';
import { openPopup } from 'utils/window';
import YoutubePlaylistModal from './components/YoutubePlaylistModal';

export const EventPlaylist = () => {
  const gamesById = useAppSelector(state => state.games.gamesById);
  const [ eventDetail, setEventDetail ] = useState({});
  const { eventId } = useParams<{eventId: string}>();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogSubmitting, setDialogSubmitting] = useState(false);

  const onDeleteVideo = useCallback((videoId: number, eventId: number) => {
    const confirmed = window.confirm("Are you sure you want to remove this video from the playlist?");
    if (confirmed) {
      dispatch(deleteDataGridRow(apiEndpoints.eventVideos, videoId, { eventId, videoId }, {}, pageKeys.eventPlaylist));
    }
  }, [dispatch]);

  const onSaveRankings = (videoIds: number[]) => {
    dispatch(saveRowRankings(apiEndpoints.eventVideos, {videoIds, eventId}, {}, pageKeys.eventPlaylist));
  }

  const onImportFromYoutubeClick = async () => {
    const authenticatedRes = await apiRequest.get(`/youtube/authenticated`);
    if (!authenticatedRes.authenticated) {
      const authUrlRes = await apiRequest.get(`/youtube/auth-url`);
      await openPopup(authUrlRes.authUrl, 'authPopup', 'width=600,height=600');
    }

    setDialogOpen(true);
  }

  const onSubmitYoutubePlaylist = async (playlistId: string | null) => {

    if (!playlistId) {
      toast.error("Please enter a valid Youtube playlist URL");
      return;
    }
    setDialogSubmitting(true);
    apiRequest.post(`/videos/import-from-youtube-playlist`, {
      playlistId,
      eventId: Number(eventId)
    }).then(res => {
      toast.success("Playlist created successfully. Please refresh the page.");
    }).catch(err => {
      toast.error(`Could not import the playlist. Error: ${err.message}`);
    }).finally(() => {
      setDialogOpen(false);
      setDialogSubmitting(false);
    })
  }

  useEffect(() => {
    apiRequest.get(`/events/${eventId}`, {
      params: {isAdmin: 1},
    }).then(data => {
      setEventDetail(data);
    }).catch(err => {
      setEventDetail({});
      toast.error(err.message);
    })
  }, [eventId]);

  const columns = useMemo(() => {
    return [
      {
        field: 'id',
        headerName: 'Id',
        width: 100,
        rowDrag: true,
      },
      {
        field: 'thumbnail',
        headerName: 'Thumbnail',
        cellRenderer: ({ data }: { data: KozoomVideo}) => <img
          src={getVideoThumbnail(data)}
          alt="thumb"
          width="100%" />,
        width: 150
      },
      {
        field: 'description',
        headerName: 'Description',
        width: 250,
      },
      {
        field: 'gameId',
        headerName: 'Game',
        valueFormatter: ({ value }: {value: number}) => getGameName(gamesById, value),
        width: 120,
      },
      {
        field: 'city',
        headerName: 'City',
        width: 120,
      },
      {
        field: 'duration',
        headerName: 'Duration',
        valueFormatter: (({ value }: {value: number}) => formatSeconds(value)),
        width: 120,
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        valueFormatter: ({ value }: {value: number}) => value && moment(value).format('Y-MM-DD'),
        width: 120,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 1,
        cellStyle: {textAlign: 'center'},
        headerClass: 'text-center',
        cellRenderer: ({ data }: {data: KozoomVideo}) => (
          <DataGridActionMenu>
            <MenuItem onClick={() => onDeleteVideo(data.id, Number(eventId))}><PlaylistDeleteForeverIcon/>Remove from playlist</MenuItem>
            <MenuItem onClick={() => history.push(`/${apiEndpoints.videos}/${data.id}`)}><EditIcon/>Edit</MenuItem>
          </DataGridActionMenu>
        ),
      },
    ];
  }, [gamesById, onDeleteVideo, eventId, history]);

  return (
    <>
      <DataGridListPage
        columns={columns as any}
        apiEndpoint={apiEndpoints.videos}
        heading={`Playlist - ${getEventName(eventDetail, gamesById)}`}
        pageKey={pageKeys.eventPlaylist}
        onAdd={() => history.push(`/${apiEndpoints.videos}`)}
        onSaveRankings={onSaveRankings}
        rowsPerPage={200}
        initialQueryParams={{
          eventId,
          isAdmin: 1,
        }}
        customHeadingToolbar={<Box mb={2}>
          <Button
            variant="contained"
            color="error"
            onClick={onImportFromYoutubeClick}
          >
            Import from Youtube
            <YoutubeIcon sx={{ ml: 1 }} />
          </Button>
        </Box>}
        gridOptions={undefined}      />
      <YoutubePlaylistModal
        open={dialogOpen}
        submitting={dialogSubmitting}
        onClose={() => setDialogOpen(false)}
        onSubmit={(playlistId: string | null) => onSubmitYoutubePlaylist(playlistId)}      
      />
    </>
  )
}

